import { addDays, format } from 'date-fns'

export type UnitStatus = 
  | 'leased'          // 🟢 Green
  | 'underpriced'     // 🟠 Orange
  | 'expiring'        // 🟡 Yellow (<= 90 days)
  | 'month_to_month'  // 🟣 Purple
  | 'transient'       // 🔵 Blue (active transient stay, no lease)
  | 'occupied_no_contract' // 🔴 Red
  | 'idle'            // ⬜ White/Gray (no lease, no transient)
  | 'maintenance'     // ⬛ Dark Gray

export type UnitType = 'Hangar' | 'Tiedown' | 'Ramp' | 'Community' | 'Office'

export type RevenueSegment = 'lease' | 'transient' | 'idle' | 'hybrid'

export interface Unit {
  id: string
  code: string
  building_id: string
  location_id: string
  type: UnitType
  sqft: number
  status: UnitStatus
  revenue_segment: RevenueSegment
  
  // Tenant Info
  tenant_name?: string
  aircraft?: string
  
  // Financials
  lease_rate?: number
  transient_revenue?: number // Monthly equivalent
  fuel_revenue?: number
  fuel_gal_pumped?: number
  fuel_gal_target?: number
  total_value?: number // lease + transient + fuel
  yield_per_sqft: number
  
  // Dates
  contract_end?: string
  days_to_expiry?: number
}

export interface Building {
  id: string
  name: string
  location_id: string
  total_units: number
  total_sqft: number
  occupancy_pct: number
  revenue: number // Space rev only (lease + transient)
  total_value: number // Revenue + fuel
  avg_yield: number // Yield per sqft without fuel
  avg_total_yield: number // Yield per sqft including fuel
  units: Unit[]
}

export interface Location {
  id: string
  name: string
  buildings: Building[]
}

export interface KpiData {
  total_yield: number
  total_value: number
  yield_annualized: number
  lease_revenue: number
  transient_revenue: number
  fuel_revenue: number
  
  occupancy_pct: number
  producing_sqft: number
  total_sqft: number
  producing_units: number
  total_units: number
  
  opportunity_monthly: number
  opportunity_annualized: number
  idle_units: number
  underpriced_units: number
  
  expiring_count: number
  expiring_risk: number
  m2m_count: number
}

// Mock Data Generators

const generateUnit = (
  id: string, 
  code: string, 
  baseSqft: number, 
  type: UnitType,
  status: UnitStatus,
  locationId: string,
  buildingId: string
): Unit => {
  
  let lease_rate = 0
  let transient_revenue = 0
  let fuel_revenue = 0
  let fuel_gal_pumped = 0
  let fuel_gal_target = 0

  let tenant_name = undefined
  let aircraft = undefined
  let contract_end = undefined
  let days_to_expiry = undefined
  
  // Logic to populate derived fields based on status
  switch(status) {
    case 'leased':
      lease_rate = baseSqft * 1.1 // High rate
      tenant_name = `Tenant ${code}`
      aircraft = 'G650'
      contract_end = '2027-12-31'
      fuel_revenue = 1500
      fuel_gal_pumped = 300
      fuel_gal_target = 250 // Exceeding target
      break
    case 'underpriced':
      lease_rate = baseSqft * 0.75 // Low rate
      tenant_name = `Budget Corp ${code}`
      aircraft = 'C172'
      contract_end = '2026-08-15'
      fuel_revenue = 6000 // High fuel despite low rent!
      fuel_gal_pumped = 1200
      fuel_gal_target = 800
      break
    case 'expiring':
      lease_rate = baseSqft * 1.05
      tenant_name = `Exiting LLC ${code}`
      aircraft = 'Falcon 900'
      contract_end = format(addDays(new Date(), 45), 'yyyy-MM-dd')
      days_to_expiry = 45
      fuel_revenue = 500 // Low fuel
      fuel_gal_pumped = 100
      fuel_gal_target = 400 // Missing target
      break
    case 'month_to_month':
      lease_rate = baseSqft * 1.2
      tenant_name = `Flex Avia ${code}`
      aircraft = 'PC-12'
      contract_end = 'M2M'
      fuel_revenue = 2500
      fuel_gal_pumped = 500
      break
    case 'transient':
      transient_revenue = baseSqft * 0.8 // Varies
      tenant_name = `Transient Guest`
      aircraft = 'Citation X'
      fuel_revenue = 400 // Transient fuel
      break
    case 'occupied_no_contract':
      tenant_name = 'Unknown'
      aircraft = 'Lear 45'
      break
    case 'idle':
      break // 0 revenue
  }

  const revenue_segment: RevenueSegment = 
    status === 'transient' ? 'transient' : 
    status === 'idle' || status === 'maintenance' ? 'idle' : 
    'lease'

  const total_rev = lease_rate + transient_revenue
  const yield_per_sqft = total_rev / baseSqft
  const total_value = total_rev + fuel_revenue

  return {
    id,
    code,
    building_id: buildingId,
    location_id: locationId,
    type,
    sqft: baseSqft,
    status,
    revenue_segment,
    tenant_name,
    aircraft,
    lease_rate: lease_rate > 0 ? lease_rate : undefined,
    transient_revenue: transient_revenue > 0 ? transient_revenue : undefined,
    fuel_revenue: fuel_revenue > 0 ? fuel_revenue : undefined,
    fuel_gal_pumped: fuel_gal_pumped > 0 ? fuel_gal_pumped : undefined,
    fuel_gal_target: fuel_gal_target > 0 ? fuel_gal_target : undefined,
    total_value,
    yield_per_sqft,
    contract_end,
    days_to_expiry
  }
}

// Building A - Hangars (Mix of Leased, Underpriced, M2M)
const unitsA: Unit[] = [
  generateUnit('u-a1', 'A-1', 2500, 'Hangar', 'leased', 'loc-phx', 'b-a'),
  generateUnit('u-a2', 'A-2', 2500, 'Hangar', 'month_to_month', 'loc-phx', 'b-a'),
  generateUnit('u-a3', 'A-3', 2500, 'Hangar', 'underpriced', 'loc-phx', 'b-a'),
  generateUnit('u-a4', 'A-4', 2500, 'Hangar', 'leased', 'loc-phx', 'b-a'),
  generateUnit('u-a5', 'A-5', 2500, 'Hangar', 'expiring', 'loc-phx', 'b-a'),
  generateUnit('u-a6', 'A-6', 2500, 'Hangar', 'idle', 'loc-phx', 'b-a'),
]

// Building B - Hangars (Transient Heavy)
const unitsB: Unit[] = [
  generateUnit('u-b1', 'B-1', 3000, 'Hangar', 'transient', 'loc-phx', 'b-b'),
  generateUnit('u-b2', 'B-2', 3000, 'Hangar', 'transient', 'loc-phx', 'b-b'),
  generateUnit('u-b3', 'B-3', 3000, 'Hangar', 'idle', 'loc-phx', 'b-b'),
]

// Tiedowns (Mix)
const unitsT: Unit[] = Array.from({ length: 10 }).map((_, i) => {
  const status: UnitStatus = i < 5 ? 'leased' : i < 8 ? 'transient' : 'idle'
  return generateUnit(`u-t${i}`, `T-${i+1}`, 400, 'Tiedown', status, 'loc-phx', 'b-t')
})


const calculateBuildingStats = (units: Unit[], name: string, id: string): Building => {
  const total_units = units.length
  const total_sqft = units.reduce((sum, u) => sum + u.sqft, 0)
  const producing_units = units.filter(u => u.status !== 'idle' && u.status !== 'maintenance' && u.status !== 'occupied_no_contract')
  const occupied_sqft = producing_units.reduce((sum, u) => sum + u.sqft, 0) // Simplified for MVP
  const revenue = units.reduce((sum, u) => sum + (u.lease_rate || 0) + (u.transient_revenue || 0), 0)
  const total_value = units.reduce((sum, u) => sum + (u.total_value || 0), 0)
  
  return {
    id,
    name,
    location_id: 'loc-phx',
    total_units,
    total_sqft,
    occupancy_pct: Math.round((occupied_sqft / total_sqft) * 100),
    revenue,
    total_value,
    avg_yield: revenue / total_sqft,
    avg_total_yield: total_value / total_sqft,
    units
  }
}

export const mockBuildings: Building[] = [
  calculateBuildingStats(unitsA, 'Hangar A', 'b-a'),
  calculateBuildingStats(unitsB, 'Hangar B', 'b-b'),
  calculateBuildingStats(unitsT, 'Tiedowns', 'b-t'),
]

export const mockKpi: KpiData = {
  total_yield: 52800,
  total_value: 78400, // including mock fuel
  yield_annualized: 633600,
  lease_revenue: 47200,
  transient_revenue: 5600,
  fuel_revenue: 25600,
  
  occupancy_pct: 82,
  producing_sqft: 48240,
  total_sqft: 58000,
  producing_units: 21,
  total_units: 25,
  
  opportunity_monthly: 8600,
  opportunity_annualized: 103200,
  idle_units: 3,
  underpriced_units: 2,
  
  expiring_count: 4,
  expiring_risk: 15600,
  m2m_count: 8
}
